import React, { Component, Fragment } from 'react';
import isNumber from 'lodash/isNumber';

import { Wrapper, InfoPopup, SoundWrapper, Label } from './styled/TrainerTranscriptWordInformationStyled';
import Detection from '../styled/Detection';
import Anonymization from '../Anonymization/TrainerTranscriptAnonymization';

const SILENCE = 'SIL';
const MIC_RESULTS = 'micResults';
const POPUP_THRESHOLD_LEFT = 10;
const POPUP_THRESHOLD_TOP = 20;

interface PronunciationItem {
    sound: string;
    confidence: number;
}

interface WordItem {
    source?: string;
    text: string;
    spotting?: string;
    anonymized?: boolean;
}

interface WordInformationProps {
    confidence?: number | null;
    pronunciation?: PronunciationItem[] | null;
    showConfidence: boolean;
    showPronunciation: boolean;
    localization: { getText: (key: string) => string };
    word: WordItem;
}

interface WordInformationState {
    popupRendered: boolean;
    leftAlignment: boolean;
    topAlignment: boolean;
    popup: HTMLElement | null;
}

class WordInformation extends Component<WordInformationProps, WordInformationState> {
    static defaultProps: Partial<WordInformationProps> = {
        confidence: null,
        pronunciation: null
    };

    shouldComponentUpdate(nextProps: WordInformationProps, nextState: WordInformationState) {
        return (
            (!this.state.popupRendered && nextState.popupRendered) ||
            (!this.state.popup && Boolean(nextState.popup)) ||
            (this.state.leftAlignment && !nextState.leftAlignment) ||
            (this.state.topAlignment && !nextState.topAlignment)
        );
    }

    state = {
        popupRendered: false,
        leftAlignment: true,
        topAlignment: true,
        popup: null as HTMLElement | null
    };

    componentDidUpdate() {
        this._setPopupPosition();
    }

    _setPopupPosition() {
        const { popup } = this.state;
        if (!popup || !this.state.leftAlignment) return;
        const offsetParent = popup.offsetParent as HTMLElement | null;
        const parent = popup.parentElement?.parentElement?.parentElement;
        if (!offsetParent || !parent) return;

        const popupOffsetLeft = offsetParent.offsetLeft - popup.offsetLeft;
        const availableSpaceLeft = window.innerWidth - popupOffsetLeft;

        const popupOffsetTop = offsetParent.offsetTop + popup.offsetTop;
        const availableSpaceTop = parent.scrollHeight - popupOffsetTop;

        this.setState({
            leftAlignment: availableSpaceLeft > popup.clientWidth + POPUP_THRESHOLD_LEFT,
            topAlignment: availableSpaceTop > popup.clientHeight + POPUP_THRESHOLD_TOP
        });
    }

    _setRenderPopup = () => {
        this.setState({ popupRendered: true });
    };

    _shouldRenderConfidence() {
        const { showConfidence, confidence } = this.props;
        return showConfidence && isNumber(confidence);
    }

    _shouldRenderPronunciation() {
        const { showPronunciation, pronunciation, word } = this.props;
        return Boolean(!word.anonymized && showPronunciation && pronunciation);
    }

    _renderConfidence() {
        const { confidence, localization } = this.props;

        if (confidence == null) {
            return null;
        }

        return (
            <div>
                <Label>{localization.getText('trainer.transcript.confidence')}:</Label>
                {` ${Math.round(confidence * 100)}%`}
            </div>
        );
    }

    _renderPronunciation() {
        const { pronunciation, localization } = this.props;

        if (!pronunciation || pronunciation.length === 0) return null;

        const sounds = pronunciation
            .filter((item: PronunciationItem) => item.sound !== SILENCE)
            .map((item: PronunciationItem, i: number) => {
                const key = `${item.confidence}_${i}`;
                return (
                    <SoundWrapper key={key} $confidence={item.confidence}>
                        {item.sound}
                    </SoundWrapper>
                );
            });

        return (
            <Fragment>
                <Label>{localization.getText('trainer.transcript.pronunciation')}</Label>
                <Label>: /</Label>
                {sounds}
                <Label>/</Label>
            </Fragment>
        );
    }

    _renderDetection() {
        const { word } = this.props;

        return word.source === MIC_RESULTS ? (
            <Detection $text={word.text} $type={word.spotting || ''}>
                <Anonymization text={word.text} anonymized={Boolean(word.anonymized)} />
            </Detection>
        ) : (
            <Anonymization text={word.text} anonymized={Boolean(word.anonymized)} />
        );
    }

    _renderPopup = (isConfidenceShown: boolean, isPronunciationShown: boolean) => {
        return this.state.popupRendered ? (
            <InfoPopup
                ref={(ref: HTMLElement | null) => {
                    this.setState({ popup: ref });
                }}
                $left={Boolean(this.state.leftAlignment)}
                $top={Boolean(this.state.topAlignment)}>
                {isConfidenceShown ? this._renderConfidence() : null}
                {isPronunciationShown ? this._renderPronunciation() : null}
            </InfoPopup>
        ) : null;
    };

    render() {
        const { confidence } = this.props;

        const isConfidenceShown = this._shouldRenderConfidence();
        const isPronunciationShown = this._shouldRenderPronunciation();

        if (!isConfidenceShown && !isPronunciationShown) return this._renderDetection();

        return (
            <Wrapper $confidence={isConfidenceShown ? (confidence ?? undefined) : undefined} onMouseEnter={this._setRenderPopup}>
                {this._renderDetection()}
                {this._renderPopup(isConfidenceShown, Boolean(isPronunciationShown))}
            </Wrapper>
        );
    }
}

export default WordInformation;
